import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';

/** Дата без времени — строка `YYYY-MM-DD`, как в API (`zDate`). */
export type IsoDate = string;

export interface DateRangeValue {
  readonly from: IsoDate;
  readonly to: IsoDate;
}

const WEEK = { weekStartsOn: 1 } as const;

/** Локальная дата → `YYYY-MM-DD` (без сдвига часового пояса). */
export function toIsoDate(date: Date): IsoDate {
  return format(date, 'yyyy-MM-dd');
}

/** `YYYY-MM-DD` → полночь этого дня по местному времени. */
export function fromIsoDate(value: IsoDate): Date {
  return parse(value, 'yyyy-MM-dd', new Date(0));
}

/** Как дата стоит в поле ввода: `28.09.2026`. */
export function formatDateInput(value: IsoDate): string {
  return format(fromIsoDate(value), 'dd.MM.yyyy');
}

const MONTHS: readonly [RegExp, number][] = [
  [/^янв/, 0],
  [/^фев/, 1],
  [/^мар/, 2],
  [/^апр/, 3],
  [/^ма[йя]/, 4],
  [/^июн/, 5],
  [/^июл/, 6],
  [/^авг/, 7],
  [/^сен/, 8],
  [/^окт/, 9],
  [/^ноя/, 10],
  [/^дек/, 11],
];

/** Номер дня недели date-fns (0 — воскресенье) по началу слова: `пт`, `пятница`, `пятницу`. */
const WEEKDAYS: readonly [RegExp, number][] = [
  [/^(пн|пон)/, 1],
  [/^вт/, 2],
  [/^ср/, 3],
  [/^(чт|чет)/, 4],
  [/^(пт|пят)/, 5],
  [/^(сб|суб)/, 6],
  [/^(вс|вос)/, 0],
];

const WORDS: Readonly<Record<string, number>> = {
  позавчера: -2,
  вчера: -1,
  сегодня: 0,
  сег: 0,
  завтра: 1,
  послезавтра: 2,
};

type Unit = 'day' | 'week' | 'month' | 'year';

function unitOf(word: string | undefined): Unit | null {
  if (word === undefined || word === '' || word.startsWith('д')) return 'day';
  if (word.startsWith('н')) return 'week';
  if (word.startsWith('м')) return 'month';
  if (/^(г|л)/.test(word)) return 'year';
  return null;
}

function shift(date: Date, amount: number, unit: Unit): Date {
  if (unit === 'week') return addWeeks(date, amount);
  if (unit === 'month') return addMonths(date, amount);
  if (unit === 'year') return addYears(date, amount);
  return addDays(date, amount);
}

/** Существующий день (31.02 — нет). */
function exactDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month, day);
  return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day ? date : null;
}

/** Год не указан: тот, при котором дата ближе к сегодняшней (в пределах полугода). */
function nearestYear(month: number, day: number, today: Date): Date | null {
  const candidate = exactDate(today.getFullYear(), month, day);
  if (candidate === null) {
    // 29.02 в невисокосный год — ближайший високосный вперёд.
    for (let year = today.getFullYear() + 1; year <= today.getFullYear() + 4; year += 1) {
      const leap = exactDate(year, month, day);
      if (leap) return leap;
    }
    return null;
  }
  if (candidate < subMonths(today, 6)) return addYears(candidate, 1);
  if (candidate > addMonths(today, 6)) return subYears(candidate, 1);
  return candidate;
}

const fullYear = (text: string) => (text.length === 2 ? 2000 + Number(text) : Number(text));

/**
 * Разбор даты, как её пишут люди: `28.09`, `28.09.26`, `2026-09-28`, `280926`, `28 сентября`,
 * `сегодня`, `завтра`, `пт`, `в пятницу`, `+3д`, `-1н`, `через 2 недели`. Не поняли — null.
 */
export function parseDateInput(raw: string, today: Date = new Date()): IsoDate | null {
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const text = raw.toLowerCase().replaceAll('ё', 'е').replace(/\s+/g, ' ').trim();
  if (text === '') return null;

  const word = WORDS[text];
  if (word !== undefined) return toIsoDate(addDays(base, word));

  // +3, +3д, -2 нед, +1м, +1г
  const relative = /^([+-])\s?(\d{1,3})\s?([а-я]*)$/.exec(text);
  if (relative) {
    const unit = unitOf(relative[3]);
    if (unit === null) return null;
    const amount = Number(relative[2]) * (relative[1] === '-' ? -1 : 1);
    return toIsoDate(shift(base, amount, unit));
  }

  // через 3 дня, через неделю, через месяц
  const after = /^через (?:(\d{1,3}) )?([а-я]+)$/.exec(text);
  if (after) {
    const unit = unitOf(after[2]);
    if (unit === null) return null;
    return toIsoDate(shift(base, Number(after[1] ?? '1'), unit));
  }

  // пт, в пятницу, след пт — ближайший такой день после сегодняшнего
  const weekday = /^(?:в |во )?(след(?:ующ[а-я]*)? )?([а-я]+)$/.exec(text);
  if (weekday) {
    const target = WEEKDAYS.find(([re]) => re.test(weekday[2] ?? ''))?.[1];
    if (target !== undefined && (weekday[2] ?? '').length >= 2 && !MONTHS.some(([re]) => re.test(text))) {
      let date = addDays(base, 1);
      while (date.getDay() !== target) date = addDays(date, 1);
      if (weekday[1]) date = addWeeks(date, 1);
      return toIsoDate(date);
    }
  }

  // 2026-09-28
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (iso) {
    const date = exactDate(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return date ? toIsoDate(date) : null;
  }

  // 28.09, 28/09/26, 28-09-2026, 28 09 2026
  const numeric = /^(\d{1,2})[./\- ](\d{1,2})(?:[./\- ](\d{2}|\d{4}))?\.?$/.exec(text);
  if (numeric) {
    const day = Number(numeric[1]);
    const month = Number(numeric[2]) - 1;
    const date = numeric[3] ? exactDate(fullYear(numeric[3]), month, day) : nearestYear(month, day, base);
    return date ? toIsoDate(date) : null;
  }

  // 280926, 28092026 — быстрый ввод без разделителей
  const compact = /^(\d{2})(\d{2})(\d{2}|\d{4})$/.exec(text);
  if (compact) {
    const date = exactDate(fullYear(compact[3] ?? ''), Number(compact[2]) - 1, Number(compact[1]));
    return date ? toIsoDate(date) : null;
  }

  // 28 сентября, 28 сен 2026
  const named = /^(\d{1,2}) ([а-я]+)\.?(?: (\d{2}|\d{4}))?$/.exec(text);
  if (named) {
    const month = MONTHS.find(([re]) => re.test(named[2] ?? ''))?.[1];
    if (month === undefined) return null;
    const day = Number(named[1]);
    const date = named[3] ? exactDate(fullYear(named[3]), month, day) : nearestYear(month, day, base);
    return date ? toIsoDate(date) : null;
  }

  // 28 — это число текущего месяца
  const dayOnly = /^(\d{1,2})$/.exec(text);
  if (dayOnly) {
    const date = exactDate(base.getFullYear(), base.getMonth(), Number(dayOnly[1]));
    return date ? toIsoDate(date) : null;
  }

  return null;
}

/**
 * Диапазон: `28.09–5.10`, `28.09 - 05.10.2026`, `с 28.09 по 5.10`, `28–30.09`
 * (у начала без месяца берётся месяц конца). Концы в обратном порядке меняются местами.
 */
export function parseDateRangeInput(raw: string, today: Date = new Date()): DateRangeValue | null {
  const text = raw.toLowerCase().trim().replace(/^с\s+/, '');
  const parts = text.split(/\s*(?:–|—|\.\.|\s-\s|\s+по\s+)\s*|(?<=\d)-(?=\d{1,2}[./])/);
  if (parts.length !== 2) return null;
  const [left = '', right = ''] = parts;
  const to = parseDateInput(right, today);
  if (to === null) return null;
  const leftDay = /^\d{1,2}$/.exec(left.trim());
  const end = fromIsoDate(to);
  const from = leftDay
    ? (() => {
        const date = exactDate(end.getFullYear(), end.getMonth(), Number(leftDay[0]));
        return date ? toIsoDate(date) : null;
      })()
    : parseDateInput(left, today);
  if (from === null) return null;
  return from <= to ? { from, to } : { from: to, to: from };
}

/** Как диапазон стоит в поле ввода: `28.09.2026 – 05.10.2026`. */
export function formatDateRangeInput(value: DateRangeValue): string {
  return `${formatDateInput(value.from)} – ${formatDateInput(value.to)}`;
}

export type DatePresetId = 'today' | 'tomorrow' | 'nextWeek' | 'monthEnd';

/** Быстрые даты под календарём. */
export function datePreset(id: DatePresetId, today: Date = new Date()): IsoDate {
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (id === 'tomorrow') return toIsoDate(addDays(base, 1));
  if (id === 'nextWeek') return toIsoDate(addWeeks(base, 1));
  if (id === 'monthEnd') return toIsoDate(endOfMonth(base));
  return toIsoDate(base);
}

export type RangePresetId =
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear';

/** Пресеты диапазона: неделя с понедельника, календарные месяц, квартал, год. */
export function rangePreset(id: RangePresetId, today: Date = new Date()): DateRangeValue {
  const range = (from: Date, to: Date) => ({ from: toIsoDate(from), to: toIsoDate(to) });
  switch (id) {
    case 'thisWeek':
      return range(startOfWeek(today, WEEK), endOfWeek(today, WEEK));
    case 'lastWeek':
      return range(startOfWeek(subWeeks(today, 1), WEEK), endOfWeek(subWeeks(today, 1), WEEK));
    case 'thisMonth':
      return range(startOfMonth(today), endOfMonth(today));
    case 'lastMonth':
      return range(startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1)));
    case 'thisQuarter':
      return range(startOfQuarter(today), endOfQuarter(today));
    case 'lastQuarter':
      return range(startOfQuarter(subQuarters(today, 1)), endOfQuarter(subQuarters(today, 1)));
    case 'thisYear':
      return range(startOfYear(today), endOfYear(today));
    case 'lastYear':
      return range(startOfYear(subYears(today, 1)), endOfYear(subYears(today, 1)));
  }
}

/** Время `HH:MM`. */
export type TimeValue = string;

/**
 * Разбор времени: `9`, `9:30`, `9.30`, `09-30`, `930`, `1745`, `9ч`, `9 ч 30`. Не поняли — null.
 */
export function parseTimeInput(raw: string): TimeValue | null {
  const text = raw.toLowerCase().replace(/\s+/g, '').replace(/ч$/, '');
  const match = /^(\d{1,2})(?:[:.\-ч](\d{2}))?(?:м(?:ин)?)?$/.exec(text) ?? /^(\d{1,2})(\d{2})$/.exec(text);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? '0');
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Время с шагом `step` минут за сутки: подсказки в поле времени. */
export function timeSlots(step = 15): TimeValue[] {
  const slots: TimeValue[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    slots.push(
      `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
    );
  }
  return slots;
}

/** Проверка строки `YYYY-MM-DD` — для значений, пришедших извне. */
export function isIsoDate(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    isValid(fromIsoDate(value)) &&
    toIsoDate(fromIsoDate(value)) === value
  );
}
